import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor, useSlideEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentAudio, PropsWithPreview, Size, SlideComponent, SlideComponentFileModel } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Box, Button } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { AudioRecorderPopover } from './audio/AudioRecorderPopover';
import { ComponentLabel } from './ComponentLabel';
import { ContextualBar } from './ContextualBar';
import { useRightClickMenu } from './hooks/useRightClickMenu';
import { useSlideComponent } from './hooks/useSlideComponent';
import { RightClickMenu } from './RightClickMenu';
import { SlideRef } from './Slide';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentAudio: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { selectedSlide } = useSlideEditor();
    const { deleteComponent, updateComponent, createSlideFile, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    // Add state for image picker popover
    const [audioRecorderAnchorEl, setAudioRecorderAnchorEl] = useState<HTMLElement | null>(null);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const onUploadAudio = React.useCallback(
        async (file: File): Promise<SlideComponentFileModel | null> => {
            try {
                return await createSlideFile(selectedSlide.editorData?.id ?? '', file);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
                return null;
            }
        },
        [createSlideFile, notifications, selectedSlide.editorData?.id],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview) {
            setAudioRecorderAnchorEl(ref.current);
        }
    }, [isFocus, preview, ref]);

    if (!isSlideComponentAudio(model)) {
        return null;
    }

    const isPlaceholder = !model?.content?.audio?.[0]?.url;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`audio-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={() => {
                if (preview) return;
                setAudioRecorderAnchorEl(ref.current);
            }}
        >
            <ResizeBorder
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                onResizeComplete={(newWidth, newHeight) => {
                    // todo save data
                    console.log('onResizeComplete', newWidth, newHeight);
                }}
                aspectRatio={1}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#audio-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.asset.audio')}
                    </ComponentLabel>
                )}

                <Box
                    ref={ref}
                    id='background-component'
                    sx={{
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {isPlaceholder ? (
                        <Box
                            sx={{
                                backgroundColor: (theme) => (theme.vars || theme).palette.grey[200],
                                aspectRatio: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: `${model.size.width}px`,
                                height: `${model.size.width}px`,
                                p: '10%',
                            }}
                        >
                            <VolumeUpIcon sx={{ color: (theme) => (theme.vars || theme).palette.grey[600], height: '90%', width: '90%' }} />
                        </Box>
                    ) : (
                        <Button
                            component={preview ? 'div' : 'button'}
                            aria-label='audio'
                            sx={{
                                border: 5,
                                width: `${model.size.width}px`,
                                height: `${model.size.width}px`,
                                borderColor: (theme) => (theme.vars || theme).palette.grey[600],
                                borderRadius: '14%',
                                color: 'black',
                                bgcolor: 'white',
                                minHeight: 48,
                                minWidth: 48,
                            }}
                            disabled={!!preview}
                        >
                            <VolumeUpIcon
                                sx={{
                                    color: (theme) => (theme.vars || theme).palette.grey[600],
                                    height: '100%',
                                    width: '100%',
                                }}
                            />
                        </Button>
                    )}
                </Box>

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <EditIcon />,
                                tooltip: t('slide.components.assets.audio.tooltip.edit'),
                                onClick: () => {
                                    setAudioRecorderAnchorEl(ref.current);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.components.assets.audio.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}

                {audioRecorderAnchorEl && (
                    <AudioRecorderPopover
                        title={t('slide.asset.audio')}
                        anchorEl={audioRecorderAnchorEl}
                        value={{
                            id: model.content?.audio[0]?.id ?? '',
                            filename: model.content?.audio[0]?.filename ?? '',
                            url: model.content?.audio[0]?.url ?? '',
                            ai: {
                                voice: model.content?.audio[0]?.ai?.voice || 'female',
                                text: model.content?.audio[0]?.ai?.text ?? '',
                            },
                        }}
                        onClose={() => {
                            setAudioRecorderAnchorEl(null);
                            setIsFocus(false);
                        }}
                        onUpdate={(audio) => {
                            const updatedContent = { ...model.content, audio: [audio] };

                            const updateModel = { ...model, content: updatedContent };

                            setTPos(updateModel.pos);
                            setTSize(updateModel.size);

                            setModel(updateModel);
                            onUpdateComponent?.(model.id, updateModel);
                        }}
                        onUpload={onUploadAudio}
                    />
                )}
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
